import { DEFAULT_LENGTH } from '../constants';

/**
 * Converts a value into a non-negative CSS pixel value string or a custom default
 *
 * This utility function normalizes various input types into valid CSS length values,
 * ensuring all outputs are non-negative pixel values or the specified default.
 *
 * @param value - Input value to convert (number, string, or undefined)
 * @param defaultValue - The default value to return for invalid or zero values (defaults to '0px')
 * @returns A valid, non-negative CSS pixel length or custom default
 *
 * @example
 * ```typescript
 * convertValueToPixels(10)              // '10px'
 * convertValueToPixels('15')            // '15px'
 * convertValueToPixels('10asdf')        // '10px'
 * convertValueToPixels('a10')           // '0px' (invalid - must start with a number)
 * convertValueToPixels(-5)              // '0px' (clamped to non-negative)
 * convertValueToPixels('invalid')       // '0px'
 * convertValueToPixels(undefined)       // '0px'
 * convertValueToPixels(0, 'auto')       // 'auto'
 * convertValueToPixels(0, '16px')       // '16px'
 * ```
 */
export const convertValueToPixels = (value?: number | string, defaultValue: string = DEFAULT_LENGTH): string => {
  if (!value) return defaultValue;

  if (typeof value === 'number' && Number.isFinite(value)) {
    const nonNegativeValue = Math.max(0, Math.floor(value));
    return nonNegativeValue === 0 ? defaultValue : `${nonNegativeValue}px`;
  }

  if (typeof value === 'string') {
    const parsedValue = parseInt(value.trim(), 10);

    if (!isNaN(parsedValue)) {
      const nonNegativeValue = Math.max(0, parsedValue);
      return nonNegativeValue === 0 ? defaultValue : `${nonNegativeValue}px`;
    }
  }

  return defaultValue;
};
